import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { createBusinessOnboarding } from '@/lib/supabaseDb';
import CityDistrictSelect from '@/components/base/CityDistrictSelect';

type Step = 'account' | 'business' | 'package' | 'done';

const STEPS: { key: Step; label: string }[] = [
  { key: 'account', label: 'Hesap Bilgileri' },
  { key: 'business', label: 'İşletme Bilgileri' },
  { key: 'package', label: 'Paket Seçimi' },
  { key: 'done', label: 'Tamamlandı' },
];

const BUSINESS_TYPES = ['Mobilya', 'Elektronik', 'Gıda', 'Tekstil', 'İnşaat', 'Otomotiv', 'Perakende', 'Hizmet', 'Diğer'];

const inputClass = 'w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all';
const labelClass = 'block text-sm font-medium text-foreground-700 mb-1.5';

export default function SignupPage() {
  const navigate = useNavigate();
  const { user, hasBusiness, loading: authLoading, refreshProfile } = useAuth();

  const [step, setStep] = useState<Step>('account');
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);

  // ---- Adım 1: Hesap Bilgileri ----
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [phone, setPhone] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState('');

  // ---- Adım 2: İşletme Bilgileri ----
  const [bizName, setBizName] = useState('');
  const [bizType, setBizType] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [taxOffice, setTaxOffice] = useState('');
  const [authorizedName, setAuthorizedName] = useState('');
  const [authorizedPhone, setAuthorizedPhone] = useState('');
  const [authorizedEmail, setAuthorizedEmail] = useState('');
  const [bizPhone, setBizPhone] = useState('');
  const [bizEmail, setBizEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [businessError, setBusinessError] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);

  // ---- Adım 3: Paket Seçimi ----
  const [packageType, setPackageType] = useState<'monthly' | 'yearly'>('monthly');

  // ---- Adım 4: Tamamlama ----
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Oturumu var ama işletmesi yok (e-posta onayı sonrası AuthGuard buraya
  // yönlendirdi) → doğrudan işletme adımından devam.
  useEffect(() => {
    if (!authLoading && user && hasBusiness === false && step === 'account') {
      setStep('business');
    }
  }, [authLoading, user, hasBusiness]); // eslint-disable-line react-hooks/exhaustive-deps

  // Zaten aktif bir işletmesi olan biri buraya gelirse panele yönlendir —
  // ama az önce onboarding'i BİZ tamamlattıysak (step === 'done') "Tamamlandı"
  // ekranının gösterilmesine izin ver, kullanıcı "Panele Git" ile kendi gitsin.
  useEffect(() => {
    if (!authLoading && user && hasBusiness === true && step !== 'done') {
      navigate('/', { replace: true });
    }
  }, [authLoading, user, hasBusiness, step, navigate]);

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountError('');

    if (!fullName.trim() || !email.trim() || !phone.trim()) {
      setAccountError('Lütfen tüm alanları doldurunuz.');
      return;
    }
    if (password.length < 6) {
      setAccountError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (password !== passwordConfirm) {
      setAccountError('Şifreler eşleşmiyor.');
      return;
    }
    if (!supabase) {
      setAccountError('Supabase bağlantısı yapılandırılmamış.');
      return;
    }

    setAccountLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { full_name: fullName.trim(), phone: phone.trim() } },
      });

      if (error) {
        setAccountError(error.message.toLowerCase().includes('already registered') || error.message.toLowerCase().includes('duplicate')
          ? 'Bu e-posta ile daha önce hesap oluşturulmuş. Giriş yapmayı deneyin.'
          : error.message);
        return;
      }

      if (data.session) {
        setStep('business');
      } else {
        // E-posta onayı gerekiyor — oturum yok, işletme adımına geçilemez.
        setAwaitingConfirmation(true);
      }
    } catch {
      setAccountError('Hesap oluşturulurken beklenmedik bir hata oluştu.');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setBusinessError('Logo 2MB\'dan küçük olmalıdır.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setBusinessError('Lütfen bir resim dosyası seçin.');
      return;
    }
    setBusinessError('');
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleBusinessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusinessError('');
    if (!bizName.trim()) {
      setBusinessError('İşletme adı zorunludur.');
      return;
    }
    setStep('package');
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      let logoUrl: string | undefined;
      if (logoFile && supabase && user) {
        const ext = logoFile.name.split('.').pop() || 'png';
        const path = `${user.id}/business-logo-${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage.from('avatars').upload(path, logoFile, { upsert: true });
        if (!uploadError) {
          logoUrl = supabase.storage.from('avatars').getPublicUrl(path).data?.publicUrl;
        }
        // Logo yüklemesi başarısız olursa sessizce atlanır — opsiyonel alan,
        // kayıt akışını engellemez.
      }

      const res = await createBusinessOnboarding({
        business: {
          name: bizName.trim(),
          type: bizType || undefined,
          tax_office: taxOffice.trim() || undefined,
          tax_number: taxNumber.trim() || undefined,
          authorized_person_name: authorizedName.trim() || undefined,
          authorized_person_phone: authorizedPhone.trim() || undefined,
          authorized_person_email: authorizedEmail.trim() || undefined,
          phone: bizPhone.trim() || undefined,
          email: bizEmail.trim() || undefined,
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          district: district.trim() || undefined,
          neighborhood: neighborhood.trim() || undefined,
          logo_url: logoUrl,
          currency: 'TRY',
        },
        package: { package_type: packageType },
      });

      if (res.error) {
        setSubmitError(res.error);
        return;
      }

      // Sıralama önemli: önce 'done' adımına geç, sonra profili yenile —
      // aksi halde hasBusiness=true olur olmaz yukarıdaki useEffect devreye
      // girip "Tamamlandı" ekranı hiç gösterilmeden panele yönlendirirdi.
      setStep('done');
      refreshProfile();
    } catch (err: any) {
      setSubmitError(err?.message || 'Kayıt tamamlanamadı. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col items-center py-8 px-4 sm:px-8">
      <div className="w-full max-w-2xl">
        {/* Logo + başlık */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground-900 font-heading">İşletmeni Kaydet</h1>
          <p className="text-sm text-foreground-400 mt-1">Kendi işletmenin yönetim panelini birkaç adımda oluştur</p>
        </div>

        {/* Adım göstergesi */}
        <div className="flex items-center justify-between mb-8 px-2">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                    i < stepIndex ? 'bg-primary-500 text-background-50' : i === stepIndex ? 'bg-primary-500 text-background-50 ring-4 ring-primary-100' : 'bg-background-100 text-foreground-400'
                  }`}
                >
                  {i < stepIndex ? <i className="ri-check-line" /> : i + 1}
                </div>
                <span className={`text-[11px] whitespace-nowrap ${i === stepIndex ? 'text-primary-600 font-medium' : 'text-foreground-400'}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 mx-2 mb-4 rounded transition-colors ${i < stepIndex ? 'bg-primary-500' : 'bg-background-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 sm:p-8">
          {/* ===== ADIM 1: HESAP BİLGİLERİ ===== */}
          {step === 'account' && !awaitingConfirmation && (
            <>
              <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-1">Hesap Bilgileri</h2>
              <p className="text-sm text-foreground-400 mb-6">Önce kendi kullanıcı hesabınızı oluşturun</p>

              {accountError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2.5">
                  <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{accountError}</p>
                </div>
              )}

              <form onSubmit={handleAccountSubmit} className="space-y-4">
                <div>
                  <label className={labelClass}>Ad Soyad</label>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Ad Soyad" className={inputClass} autoComplete="name" />
                </div>
                <div>
                  <label className={labelClass}>E-posta</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@sirket.com" className={inputClass} autoComplete="email" />
                </div>
                <div>
                  <label className={labelClass}>Telefon</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05XX XXX XX XX" className={inputClass} autoComplete="tel" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Şifre</label>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="En az 6 karakter" className={inputClass} autoComplete="new-password" />
                  </div>
                  <div>
                    <label className={labelClass}>Şifre Tekrarı</label>
                    <input type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} placeholder="Şifrenizi tekrar yazın" className={inputClass} autoComplete="new-password" />
                  </div>
                </div>

                <button type="submit" disabled={accountLoading} className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold transition-all cursor-pointer hover:bg-primary-600 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed">
                  {accountLoading ? (
                    <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />Hesap oluşturuluyor...</span>
                  ) : 'Devam Et'}
                </button>
              </form>

              <div className="mt-5 text-center">
                <button type="button" onClick={() => navigate('/login')} className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                  Zaten hesabınız var mı? Giriş yapın
                </button>
              </div>
            </>
          )}

          {/* ===== E-POSTA ONAYI BEKLENİYOR ===== */}
          {step === 'account' && awaitingConfirmation && (
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
                <i className="ri-mail-check-line text-2xl text-primary-500" />
              </div>
              <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-2">E-postanızı Onaylayın</h2>
              <p className="text-sm text-foreground-500 max-w-sm mx-auto mb-6">
                <strong>{email}</strong> adresine bir onay bağlantısı gönderdik. Onayladıktan sonra giriş yapın —
                işletme kaydınıza kaldığınız yerden devam edeceksiniz.
              </p>
              <button type="button" onClick={() => navigate('/login')} className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold cursor-pointer hover:bg-primary-600">
                Giriş Sayfasına Git
              </button>
            </div>
          )}

          {/* ===== ADIM 2: İŞLETME BİLGİLERİ ===== */}
          {step === 'business' && (
            <>
              <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-1">İşletme Bilgileri</h2>
              <p className="text-sm text-foreground-400 mb-6">İşletmenizin temel bilgilerini girin</p>

              {businessError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2.5">
                  <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{businessError}</p>
                </div>
              )}

              <form onSubmit={handleBusinessSubmit} className="space-y-4">
                <div className="flex items-center gap-4">
                  <div
                    className="w-16 h-16 rounded-xl bg-background-100 border border-dashed border-background-300 flex items-center justify-center overflow-hidden shrink-0 cursor-pointer"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-image-add-line text-xl text-foreground-300" />
                    )}
                  </div>
                  <div>
                    <button type="button" onClick={() => logoInputRef.current?.click()} className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                      Logo Yükle (opsiyonel)
                    </button>
                    <p className="text-xs text-foreground-400 mt-0.5">PNG/JPG, en fazla 2MB</p>
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>İşletme Adı</label>
                  <input type="text" value={bizName} onChange={(e) => setBizName(e.target.value)} placeholder="Örn. Yılmaz Mobilya" className={inputClass} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>İşletme Türü / Sektör</label>
                    <select value={bizType} onChange={(e) => setBizType(e.target.value)} className={`${inputClass} cursor-pointer`}>
                      <option value="">Seçiniz</option>
                      {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Vergi Numarası</label>
                    <input type="text" value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} placeholder="Vergi No" className={inputClass} />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Vergi Dairesi</label>
                  <input type="text" value={taxOffice} onChange={(e) => setTaxOffice(e.target.value)} placeholder="Vergi Dairesi" className={inputClass} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Yetkili Kişi</label>
                    <input type="text" value={authorizedName} onChange={(e) => setAuthorizedName(e.target.value)} placeholder="Ad Soyad" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Yetkili Telefon</label>
                    <input type="tel" value={authorizedPhone} onChange={(e) => setAuthorizedPhone(e.target.value)} placeholder="05XX XXX XX XX" className={inputClass} />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Yetkili E-posta</label>
                  <input type="email" value={authorizedEmail} onChange={(e) => setAuthorizedEmail(e.target.value)} placeholder="yetkili@sirket.com" className={inputClass} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>İşletme Telefonu</label>
                    <input type="tel" value={bizPhone} onChange={(e) => setBizPhone(e.target.value)} placeholder="0XXX XXX XX XX" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>İşletme E-postası</label>
                    <input type="email" value={bizEmail} onChange={(e) => setBizEmail(e.target.value)} placeholder="info@sirket.com" className={inputClass} />
                  </div>
                </div>

                <CityDistrictSelect city={city} district={district} neighborhood={neighborhood} onCityChange={setCity} onDistrictChange={setDistrict} onNeighborhoodChange={setNeighborhood} selectClassName={inputClass} />

                <div>
                  <label className={labelClass}>Açık Adres</label>
                  <textarea value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Mahalle, cadde, no..." rows={2} className={inputClass} />
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className={labelClass}>Para Birimi</label>
                    <input type="text" value="TRY" disabled className={`${inputClass} bg-background-100 text-foreground-400 cursor-not-allowed`} />
                  </div>
                  <div>
                    <label className={labelClass}>Durum</label>
                    <input type="text" value="Aktif" disabled className={`${inputClass} bg-background-100 text-foreground-400 cursor-not-allowed`} />
                  </div>
                </div>

                <button type="submit" className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold transition-all cursor-pointer hover:bg-primary-600 active:scale-[0.98]">
                  Devam Et
                </button>
              </form>
            </>
          )}

          {/* ===== ADIM 3: PAKET SEÇİMİ ===== */}
          {step === 'package' && (
            <>
              <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-1">Paket Seçimi</h2>
              <p className="text-sm text-foreground-400 mb-6">Size uygun paketi seçin — fiyatlandırma yakında eklenecek</p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {([
                  { key: 'monthly' as const, label: 'Aylık Paket', icon: 'ri-calendar-line', desc: 'Esnek, ihtiyaca göre her ay yenilenir' },
                  { key: 'yearly' as const, label: 'Yıllık Paket', icon: 'ri-calendar-2-line', desc: 'Uzun vadeli kullanım için avantajlı' },
                ]).map((pkg) => (
                  <button
                    key={pkg.key}
                    type="button"
                    onClick={() => setPackageType(pkg.key)}
                    className={`text-left p-5 rounded-xl border-2 transition-all cursor-pointer ${
                      packageType === pkg.key ? 'border-primary-500 bg-primary-50/50' : 'border-background-200 hover:border-background-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${packageType === pkg.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                        <i className={`${pkg.icon} text-lg`} />
                      </div>
                      {packageType === pkg.key && <i className="ri-checkbox-circle-fill text-primary-500 text-xl" />}
                    </div>
                    <h3 className="font-semibold text-foreground-900 font-heading mb-1">{pkg.label}</h3>
                    <p className="text-xs text-foreground-400 mb-3">{pkg.desc}</p>
                    <p className="text-sm font-medium text-foreground-500">Fiyatlandırma sonradan eklenecek</p>
                  </button>
                ))}
              </div>

              {submitError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2.5">
                  <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep('business')} className="flex-1 py-2.5 bg-background-100 text-foreground-700 rounded-lg text-sm font-semibold cursor-pointer hover:bg-background-200">
                  Geri
                </button>
                <button
                  type="button"
                  onClick={handleFinalSubmit}
                  disabled={submitting}
                  className="flex-1 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold cursor-pointer hover:bg-primary-600 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />Tamamlanıyor...</span>
                  ) : 'Kaydı Tamamla'}
                </button>
              </div>
            </>
          )}

          {/* ===== ADIM 4: TAMAMLANDI ===== */}
          {step === 'done' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-3xl text-emerald-500" />
              </div>
              <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-2">İşletmeniz Oluşturuldu!</h2>
              <p className="text-sm text-foreground-500 max-w-sm mx-auto mb-6">
                <strong>{bizName}</strong> işletmeniz {packageType === 'monthly' ? 'aylık' : 'yıllık'} paket ile başarıyla oluşturuldu. Artık panelinize erişebilirsiniz.
              </p>
              <button type="button" onClick={() => navigate('/', { replace: true })} className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold cursor-pointer hover:bg-primary-600">
                Panele Git
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
